/**
 * A textarea that can underline parts of what has been typed into it.
 *
 * A textarea renders plain text and nothing else — there is no way to style a
 * range inside one. The standard answer, used here, is a mirror: a div behind
 * the field holding the same text with the same metrics, carrying the markup,
 * with the real textarea transparent on top so the caret, selection, spellcheck
 * and native editing all still belong to the textarea.
 *
 * The two must agree exactly on font, padding, border, wrapping and scroll
 * position, or the highlight drifts away from the words it marks. That is the
 * whole difficulty of the technique and the reason it is isolated here rather
 * than repeated inline wherever it is wanted.
 */
import { useLayoutEffect, useRef, type CSSProperties } from "react";

export type HighlightRange = { start: number; end: number };

export default function HighlightedTextarea({
  id,
  value,
  onChange,
  placeholder,
  highlights,
  style,
  highlightStyle,
  ariaDescribedBy,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Character ranges to mark, in ascending order and non-overlapping. */
  highlights: HighlightRange[];
  style: CSSProperties;
  highlightStyle?: CSSProperties;
  ariaDescribedBy?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  // The mirror scrolls with the field; without this the marks stay put while
  // the text moves under them.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !mirror) return;
    const sync = () => {
      mirror.scrollTop = textarea.scrollTop;
      mirror.scrollLeft = textarea.scrollLeft;
    };
    sync();
    textarea.addEventListener("scroll", sync);
    return () => textarea.removeEventListener("scroll", sync);
  }, [value]);

  const segments: Array<{ text: string; marked: boolean }> = [];
  let cursor = 0;
  for (const range of highlights) {
    const start = Math.max(cursor, range.start);
    const end = Math.max(start, range.end);
    if (start > cursor) segments.push({ text: value.slice(cursor, start), marked: false });
    if (end > start) segments.push({ text: value.slice(start, end), marked: true });
    cursor = end;
  }
  if (cursor < value.length) segments.push({ text: value.slice(cursor), marked: false });

  /*
   * Everything that affects where a glyph lands must be identical in both
   * layers. `whiteSpace: pre-wrap` and `wordBreak: break-word` match a
   * textarea's own wrapping; without them a long address wraps in one layer and
   * not the other.
   */
  const sharedMetrics: CSSProperties = {
    ...style,
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mirrorRef}
        aria-hidden="true"
        style={{
          ...sharedMetrics,
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          background: style.background,
          // Invisible: the readable text is the textarea's own, on top. The
          // mirror exists only to carry the underline beneath it.
          color: "transparent",
        }}
      >
        {segments.map((segment, index) =>
          segment.marked
            ? <span key={index} style={{ color: "transparent", ...highlightStyle }}>{segment.text}</span>
            : <span key={index}>{segment.text}</span>,
        )}
        {/* A trailing newline is not rendered by a div; this keeps the last line aligned. */}
        {value.endsWith("\n") ? "​" : null}
      </div>

      <textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        aria-describedby={ariaDescribedBy}
        style={{
          ...sharedMetrics,
          position: "relative",
          background: "transparent",
          // The mirror behind supplies the surface; a second opaque background
          // here would hide the marks entirely.
        }}
      />
    </div>
  );
}
